<template>
	<div v-if="loading" class="burn-up-chart__loading-container">
		<SuiLoadingCircle size="medium" />
	</div>

	<ProjectReportsChartEmptyState
		v-else-if="shouldShowEmptyState && project"
		:illustration-src="
			Assets.PROJECT_REPORTS_BURN_UP_CHART_EMPTY_ILLUSTRATION
		"
		:content="t('project_reports_burn_up_chart_empty_state_content')"
		:project="project"
		:retainer-period-id="retainerPeriodId"
		icon="icon-line-chart-24"
	/>
	<div v-else>
		<div :class="{ 'burn-up-chart__container': hasSidebar }">
			<div
				:class="{
					'burn-up-chart__content-in-grid': isViewedInGrid,
				}"
			>
				<ChartFilterBar class="burn-up-chart__filter-bar">
					<template #header>
						<h3>{{ $t('project_burn_up_chart') }}</h3>
						<SuiInfoButton
							v-if="!isViewedInGrid"
							as="a"
							:href="REPORTS_HELP_ARTICLE_URL"
						>
							{{ $t('project_learn_more') }}
						</SuiInfoButton>
					</template>
				</ChartFilterBar>

				<div
					class="burn-up-chart__chart-container"
					:class="{
						'burn-up-chart__chart-container__classic':
							!isViewedInGrid,
					}"
				>
					<Chart
						:key="`chart-burnup-${projectId}-${retainerPeriodId}`"
						:options="chartOptions"
						:class="[isHoveringChart ? 'burn-up-chart__hover' : '']"
						data-testid="burn-up-chart-chart"
						v-bind="chartProps"
					/>
				</div>
			</div>
			<div
				v-if="hasSidebar"
				class="burn-up-chart__topics-container"
				data-testid="chart-metrics"
				:class="{
					'burn-up-chart__topics-container--with-warning':
						showWarningOnSidebar,
				}"
			>
				<SuiSummaryTopic
					v-for="(topic, i) in sideBarTopics"
					:key="i"
					:heading="topic.heading"
					:tooltip="topic.tooltip"
					:value="topic.value"
					:value-state="topic.valueState"
					:unit="topic.unit"
					:secondary-value="topic.secondaryValue"
					:secondary-description="topic.secondaryDescription"
					:secondary-value-state="topic.secondaryValueState"
					:secondary-unit="topic.secondaryUnit"
					:icon-name="topic.iconName"
					:is-secondary-value-first="topic.isSecondaryValueFirst"
					class="burn-up-chart__summary-topic"
				/>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { ChartFilterBar } from '@/features/App';
import {
	SuiInfoButton,
	SuiLoadingCircle,
	SuiSummaryTopic,
	type SuiSummaryTopicProps,
} from '@developers/scoro-ui';
import { computed, ref, toRefs } from 'vue';
import { Chart } from 'highcharts-vue';
import { type Options, type SeriesOptionsType } from 'highcharts';
import { useFormatter, useTranslation } from '@/composables';
import {
	accessibilityForDuration,
	defaultOptions,
	plotOptionsDisableClickingOnLegend,
	chartHighlightSeriesOnLegendHover,
	legendTranslated,
	type YAxisOptionsParamsFunction,
} from '@/lib/highchartsUtils';
import { parse } from 'date-fns';
import { resetTime } from '@/utils/formatting';
import { Assets } from '@/constants/assets';
import highchartsMore from 'highcharts/highcharts-more';
import ProjectReportsChartEmptyState from '../ProjectReportsChartEmptyState.vue';
import { useTrackChartInteractionEvent } from '../composables';
import { AmplitudeCustomEvents } from '@/lib/amplitude';
import { REPORTS_HELP_ARTICLE_URL } from '../../constants';
import { type ChartProgressGroup, type ChartProject } from '../types';
import * as Highcharts from 'highcharts';
import { PROGRESS_DATA_DATE_FORMAT } from '../constants';
highchartsMore(Highcharts);
import { type RetainerPeriod, type Project } from '@/generated/graphql';

const props = defineProps<{
	projectId: Project['id'];
	retainerPeriodId?: RetainerPeriod['id'];
	makeYAxisDurationOptions: YAxisOptionsParamsFunction;
	isViewedInGrid?: boolean;
	hasSidebar?: boolean;
	sideBarTopics?: SuiSummaryTopicProps[];
	maxAxisValue?: number;
	project?: ChartProject;
	loading?: boolean;
	budgetPlotLines: Highcharts.YAxisPlotLinesOptions[];
	plotLines: Highcharts.SeriesOptionsType[];
	gradientLines: Highcharts.DefsOptions;
	tooltipOptions: Highcharts.TooltipOptions;
	progress: ChartProgressGroup[];
	secondsPerTick: number;
}>();

const {
	project,
	progress,
	gradientLines,
	makeYAxisDurationOptions,
	maxAxisValue,
	secondsPerTick,
	budgetPlotLines,
	plotLines,
	tooltipOptions,
} = toRefs(props);

const { t } = useTranslation();
const { formatLongDuration } = useFormatter();

/**
 * The format of the group name date from the BE is non-standard, in the format
 * of yyyyMMdd. E.g., 20230506.
 */

const parseGroupNameDate = (date: string) => {
	return parse(date, PROGRESS_DATA_DATE_FORMAT, new Date());
};

const firstProgressGroup = computed<ChartProgressGroup | undefined>(
	() => progress.value[0]
);

const showWarningOnSidebar = computed(() => {
	//TODO: When doing  https://landmark.scoro.ee/tasks/view/2199624, add logic here for returning true when
	//warning should be shown
	return false;
});

const TODAY = resetTime(new Date(Date.now()));

const firstProgressGroupDate = computed(() => {
	return firstProgressGroup.value
		? parseGroupNameDate(firstProgressGroup.value.groupName)
		: undefined;
});
const shouldShowEmptyState = computed(() => {
	return (
		!project?.value?.deadline &&
		!progress.value.some(
			(oneProgress) =>
				oneProgress.actualValue ||
				oneProgress.plannedValue ||
				oneProgress.forecastedValue ||
				oneProgress.actualCost ||
				oneProgress.forecastedCost
		)
	);
});

const chartStartTime = computed(() => {
	return firstProgressGroupDate.value?.getTime();
});

/**
 * When the chart series are being hovered, then we don't want to show any as
 * inactive. When the LEGENDS are hovered, then we DO want to highlight the
 * hovered series. So, we need to add an extra class to the chart when the
 * SERIES are hovered, but not the legend.
 */
const isHoveringChart = ref(false);

const chartOptions = computed<Options>(() =>
	defaultOptions({
		chart: chartHighlightSeriesOnLegendHover(),
		accessibility: accessibilityForDuration({
			formatLongDuration,
		}),
		time: {
			useUTC: false,
		},
		plotOptions: plotOptionsDisableClickingOnLegend({
			series: {
				marker: {
					enabled: false,
				},
				pointStart: chartStartTime.value,
				pointIntervalUnit: 'day',
				legendSymbol: 'rectangle',
				events: {
					mouseOver: () => (isHoveringChart.value = true),
					mouseOut: () => (isHoveringChart.value = false),
				},
			},
		}),
		defs: gradientLines.value,
		xAxis: {
			type: 'datetime',
			plotLines: [
				{
					value: TODAY.getTime(),
					zIndex: 5,
					className: 'burn-up-chart__today-line',
					label: {
						verticalAlign: 'top',
						align: 'center',
						rotation: 0,
						x: 0,
						y: 12,
						useHTML: true,
						formatter() {
							return `
								<div class="burn-up-chart__today-line-text">
									${t('today')}
								</div>`;
						},
					},
				},
			],
		},
		yAxis: makeYAxisDurationOptions.value({
			formatLongDuration,
			maxAxisValue: maxAxisValue.value,
			secondsPerTick: secondsPerTick.value,
			plotLines: budgetPlotLines.value,
		}),
		legend: legendTranslated(),

		series: plotLines.value.filter(Boolean) as SeriesOptionsType[],
		tooltip: tooltipOptions.value,
	})
);

const chartProps = useTrackChartInteractionEvent(
	AmplitudeCustomEvents.ProjectLineChartInteraction
);
</script>

<style lang="scss" scoped>
.burn-up-chart {
	&__container {
		display: flex;
		flex-direction: column;
		gap: 2rem;
	}

	&__content-in-grid {
		padding: 3rem;
		box-shadow: $scoro-box-shadow;
		border-radius: 0.5rem;
	}

	&__topics-container {
		--sui-summary-topic-min-width: fit-content;

		flex: 0 0 auto;
		flex-flow: row wrap;
		display: flex;
		gap: 2rem;

		&--with-warning {
			flex: 0 0 auto;
			flex-flow: row wrap;
		}
	}

	&__filter-bar {
		margin-left: -3rem;
		margin-right: -3rem;
		padding-left: 3rem;
		padding-right: 3rem;
		margin-bottom: 5rem;

		&:empty {
			display: none;
		}
	}

	&__loading-container {
		display: flex;
		justify-content: center;
		padding-top: 3rem;
		padding-bottom: 3rem;
	}

	&__chart-container {
		margin-left: -3rem;
		margin-right: -3rem;
		padding-left: 3rem;
		padding-right: 3rem;

		&__classic {
			padding-top: 4rem;
			border-top: 1px solid $neutral-20;
		}
	}

	&__summary-topic {
		box-shadow: $scoro-box-shadow;
		border-radius: 0.5rem;
	}

	&__hover {
		--highcharts-series-inactive-opacity: 1;
	}
}
</style>
